import "./CursoTable.css";
import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { IoIosAddCircle } from "react-icons/io";
import Loading from "../0. Utilidades/loadingSpinner";

function normalizeString(val) {
  if (val == null) return "";
  return String(val)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function getApprovalStats(rowData) {
  const elegidasCount = rowData.elegidas_count || 0;
  const aprobadasCount = rowData.aprobadas_count || 0;

  const approvedPercentage =
    elegidasCount > 0 ? Math.round((aprobadasCount / elegidasCount) * 100) : 0;

  return {
    elegidasCount,
    aprobadasCount,
    approvedPercentage,
    notApprovedPercentage: 100 - approvedPercentage,
  };
}

function PerfilesTable({
  apiUrl,
  onRowClick,
  category,
  initialPageIndex = 0,
  onPageChange,
  pageSize: externalPageSize = 10,
  onPageSizeChange,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters (all active)
  const [comunaFilter, setComunaFilter] = useState("");
  const [oficioFilter, setOficioFilter] = useState("");
  const [extraFilter, setExtraFilter] = useState("");

  const extraFilterLabel = "Empresa";
  const extraFilterKey = "empresa";

  // 2 visible table columns only
  const tableColumns = useMemo(
    () => [
      {
        Header: "Fecha",
        accessor: "f_inicio",
        sortType: (rowA, rowB, columnId) => {
          const a = new Date(rowA.values[columnId] || 0).getTime();
          const b = new Date(rowB.values[columnId] || 0).getTime();
          return a === b ? 0 : a > b ? 1 : -1;
        },
        Cell: ({ value }) => {
          if (!value) return "";
          const d = new Date(value);
          return isNaN(d) ? String(value) : d.toLocaleDateString("es-CL");
        },
      },
      {
        Header: "Nombre",
        accessor: "nombre",
        Cell: ({ row }) => {
  const { nombre, etiqueta, empresa, comuna, oficio } = row.original;

  const {
    elegidasCount,
    aprobadasCount,
    approvedPercentage,
    notApprovedPercentage,
  } = getApprovalStats(row.original);

  const showApprovalBar = elegidasCount > 0;

  return (
    <div className="nombre-cell d-flex flex-column gap-1">
      <div className="d-flex flex-wrap align-items-center gap-1">
        <div className="badges d-flex flex-wrap gap-1">
          {empresa ? <span className="badge bg-primary">{empresa}</span> : null}
          {comuna ? <span className="badge bg-danger">{comuna}</span> : null}
          {oficio ? (
            <span className="badge bg-warning text-dark">{oficio}</span>
          ) : null}
          {etiqueta ? (
            <span className="badge bg-secondary">{etiqueta}</span>
          ) : null}
        </div>

        <span className="nombre-text">{nombre}</span>
      </div>

      {showApprovalBar ? (
        <>
          <div
            className="approval-mini-bar"
            title={`${aprobadasCount}/${elegidasCount} aprobadas (${approvedPercentage}%)`}
          >
            <div
              className="approval-mini-bar-approved"
              style={{ width: `${approvedPercentage}%` }}
            />
            <div
              className="approval-mini-bar-not-approved"
              style={{ width: `${notApprovedPercentage}%` }}
            />
          </div>

          <div className="approval-mini-bar-label">
            {aprobadasCount}/{elegidasCount} aprobadas · {approvedPercentage}%
          </div>
        </>
      ) : null}
    </div>
  );
},
      },
    ],
    []
  );

  // Server-side fetch with active filters
  useEffect(() => {
    setIsLoading(true);
    const base = window.location.origin;
    const url = new URL(apiUrl, base);
    if (comunaFilter) url.searchParams.append("comuna", comunaFilter);
    if (oficioFilter) url.searchParams.append("oficio", oficioFilter);
    if (extraFilter) url.searchParams.append(extraFilterKey, extraFilter);

    fetch(url.toString())
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setIsLoading(false);
      });
  }, [apiUrl, comunaFilter, oficioFilter, extraFilter, extraFilterKey]);

  // Client-side fallback + search (search only over Fecha/Nombre)
  const filteredData = useMemo(() => {
    const normalizedSearchTerm = normalizeString(searchQuery);

    return data.filter((item) => {
      // A) client-side fallback for filters
      if (comunaFilter && item.comuna !== comunaFilter) return false;
      if (oficioFilter && item.oficio !== oficioFilter) return false;
      if (extraFilter && item[extraFilterKey] !== extraFilter) return false;
      

      // B) search only within the two visible columns
      if (!normalizedSearchTerm) return true;

      const fieldsToSearch = {
        f_inicio: item.f_inicio,
        nombre: item.nombre,
      };

      return Object.values(fieldsToSearch).some((val) =>
        normalizeString(val).includes(normalizedSearchTerm),
      );
    });
  }, [
    data,
    searchQuery,
    comunaFilter,
    oficioFilter,
    extraFilter,
    extraFilterKey,
  ]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: tableColumns,
      data: filteredData,
      initialState: { pageIndex: initialPageIndex, pageSize: externalPageSize },
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    onPageChange?.(pageIndex);
  }, [pageIndex, onPageChange]);

  useEffect(() => {
    onPageSizeChange?.(pageSize);
  }, [pageSize, onPageSizeChange]);

  return (
    <>
      <div className="generic-table-header">
        <h5>
          Buscador de {category.charAt(0).toUpperCase() + category.slice(1)}
        </h5>
      </div>

      <div className="generic-table-wrapper">
        <a
          href={`/api/perfiles/anadir_${category}/`}
          className="btn btn-secondary mb-3 add-item-button"
        >
          Agregar Nuevo <IoIosAddCircle />
        </a>

        {/* Active filters: comuna, oficio, extra */}
        <div className="filter-controls d-flex gap-2">
        <select
            className="form-select border-primary"
            value={extraFilter}
            onChange={(e) => setExtraFilter(e.target.value)}
          >
            <option value="">{`Todas las ${extraFilterLabel}`}</option>
            {
              [...new Set(data.map((d) => d[extraFilterKey]))]
                .filter(Boolean)
                .sort((a, b) =>
                  a.localeCompare(b, "es", { sensitivity: "base" }),
                )
                .map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))
            }
          </select>
          <select
            className="form-select border-danger"
            value={comunaFilter}
            onChange={(e) => setComunaFilter(e.target.value)}
          >
            <option value="">Todas las Comunas</option>
            {[...new Set(data.map((d) => d.comuna))]
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
              .map((comuna) => (
                <option key={comuna} value={comuna}>
                  {comuna}
                </option>
              ))}
          </select>

          <select
            className="form-select border-warning"
            value={oficioFilter}
            onChange={(e) => setOficioFilter(e.target.value)}
          >
            <option value="">Todos los Oficios</option>
            {[...new Set(data.map((d) => d.oficio))]
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
              .map((oficio) => (
                <option key={oficio} value={oficio}>
                  {oficio}
                </option>
              ))}
          </select>

          
        </div>

        {/* Keep the button intact */}
        <button
          className="filter-controls btn btn-outline-success"
          onClick={() => {
            setComunaFilter("");
            setOficioFilter("");
            setExtraFilter("");
          }}
        >
          Limpiar Filtros
        </button>

        <div className="generic-table">
          <div className="generic-search-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-button"
                onClick={() => setSearchQuery("")}
              >
                &times;
              </button>
            )}
          </div>

          {isLoading ? (
            <Loading />
          ) : (
            <>
              <div className="generic-pagination">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                      Mostrar {size}
                    </option>
                  ))}
                </select>
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                  {"<"}
                </button>
                <span>
                  {" "}
                  Página {pageIndex + 1} de {pageOptions.length}{" "}
                </span>
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                  {">"}
                </button>
              </div>

              <div className="badge-legend d-flex flex-wrap align-items-center gap-2 my-2">
  <span className="me-1 fw-semibold">Leyenda:</span>
  
  <span className="badge bg-primary">Empresa</span>
  <span className="badge bg-danger">Comuna</span>
  <span className="badge bg-warning text-dark">Oficio</span>
  <span className="badge bg-secondary">Etiqueta</span>
</div>

              <table {...getTableProps()} className="table table-striped">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        onClick={() => onRowClick(row.original)}
                      >
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default PerfilesTable;
